import * as React from 'react'
import { Check, X } from 'lucide-react'
import { sb } from '@/lib/supabase'
import type { Test, TestResult } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogDescription,
  DialogFullScreenContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * Taking a test, and reading the result.
 *
 * Grading happens in the database (`app.submit_test_result`), not here. The
 * answer key does ship to the client — every signed-in user can read
 * `public.tests` — but nothing in this component looks at it, so the score a
 * person sees is the score the database computed and stored. See the note in
 * 0037 for why that trade is acceptable for a comprehension check, and what to
 * change first if these ever gate something that matters.
 */
export function TestRunner({
  test,
  teamId,
  onClose,
  onSubmitted,
}: {
  test: Test
  teamId: string
  onClose: () => void
  onSubmitted: () => void
}) {
  const [answers, setAnswers] = React.useState<Record<string, string>>({})
  const [result, setResult] = React.useState<TestResult | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const questions = test.questions ?? []
  const answered = questions.filter((q) => answers[q.id]).length
  const allAnswered = answered === questions.length && questions.length > 0

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const { data, error: err } = await sb().rpc('submit_test_result', {
        p_test_id: test.id,
        p_team_id: teamId,
        p_answers: answers,
      })
      if (err) throw err
      setResult(data as TestResult)
      // Refresh the page behind this one: a pass may have completed the path.
      onSubmitted()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // Answers already given, not yet submitted. Once the result is back there is
  // nothing left to lose, and closing a graded test should just close it.
  const guard = useDiscardGuard(!result && Object.keys(answers).length > 0, onClose)

  const detailFor = (id: string) => result?.detail?.find((d) => d.id === id)

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogFullScreenContent>
        <DiscardPrompt guard={guard} what="your answers" />
        <div className="flex h-full flex-col">
          <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">{test.title}</DialogTitle>
              <DialogDescription className="truncate text-xs">
                {result
                  ? `${result.score} of ${result.total} correct — ${result.passed ? 'passed' : 'not passed'}`
                  : `${questions.length} questions · ${Math.ceil(test.pass_mark * questions.length)} correct to pass`}
              </DialogDescription>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {result ? (
                <Button onClick={onClose}>Done</Button>
              ) : (
                <>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {answered} / {questions.length}
                  </span>
                  <Button variant="ghost" onClick={guard.requestClose}>
                    Cancel
                  </Button>
                  <Button onClick={() => void submit()} disabled={!allAnswered || busy}>
                    Submit
                  </Button>
                </>
              )}
              <Button variant="ghost" size="icon-sm" onClick={guard.requestClose} aria-label="Close">
                <X />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-2xl space-y-6">
              {result && (
                <div
                  className={cn(
                    'rounded-lg border p-4',
                    result.passed
                      ? 'border-primary/40 bg-primary/5'
                      : 'border-warning/40 bg-warning/5',
                  )}
                >
                  <div className="text-sm font-medium">
                    {result.passed
                      ? 'Passed. This step of the program is done.'
                      : 'Not passed yet — read the notes below and take it again.'}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {result.score} of {result.total} correct. Every attempt is kept; the best one counts.
                  </div>
                </div>
              )}

              {questions.map((q, i) => {
                const detail = detailFor(q.id)
                return (
                  <div key={q.id} className="space-y-2">
                    <div className="flex items-start gap-2 text-sm font-medium">
                      <span className="tabular-nums text-muted-foreground">{i + 1}.</span>
                      <span className="min-w-0 flex-1">{q.prompt}</span>
                      {detail &&
                        (detail.correct ? (
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        ) : (
                          <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        ))}
                    </div>

                    <div className="space-y-1.5 pl-6">
                      {q.choices.map((c) => {
                        const chosen = (result ? detail?.given : answers[q.id]) === c.id
                        // After grading, mark the right answer whether or not it
                        // was chosen — being told only "wrong" teaches nothing.
                        const isAnswer = Boolean(result && detail && detail.answer === c.id)
                        return (
                          <button
                            key={c.id}
                            type="button"
                            disabled={Boolean(result) || busy}
                            onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: c.id }))}
                            className={cn(
                              'flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                              !result && 'hover:bg-accent',
                              chosen && !result && 'border-primary bg-primary/5',
                              isAnswer && 'border-primary bg-primary/10',
                              result && chosen && !detail?.correct && 'border-destructive bg-destructive/5',
                            )}
                          >
                            <span className="mt-0.5 font-mono text-xs uppercase text-muted-foreground">
                              {c.id}
                            </span>
                            <span className="min-w-0 flex-1">{c.text}</span>
                          </button>
                        )
                      })}
                    </div>

                    {detail?.why && (
                      <p className="pl-6 text-xs text-muted-foreground">{detail.why}</p>
                    )}
                  </div>
                )
              })}

              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          </div>
        </div>
      </DialogFullScreenContent>
    </Dialog>
  )
}
